import { type CheckoutScope } from '@impact/types';

const ADYEN_REDIRECT_SESSION_STORAGE_PREFIX = 'adyen-redirect-session';

export interface StoredAdyenRedirectSession {
  donationId: string;
  sessionId: string;
  sessionData: string;
  donorEmail: string;
  flow?: 'donation' | 'registration' | 'items';
  /** Donation amount for display on redirect return (form state is lost on reload) */
  amount: number;
  currencySymbol: string;
  savedAt: number;
  /** Fields needed for commitCheckout on redirect return */
  commitInputs?: {
    donorFirstName: string;
    donorLastName: string;
    donorCompanyName?: string;
    donorDisplayName?: string;
    message?: string;
    isAnonymous: boolean;
    giftAidEligible: boolean;
    returnUrl: string;
    donorCountryCode?: string;
    donorAddress?: {
      streetAddress: string;
      city: string;
      state?: string;
      postalCode: string;
      country?: string;
      addressLine2?: string;
    };
    donorTaxNumber?: string;
    platformFee?: number;
    scope: CheckoutScope;
    items?: Array<{ itemId: string; quantity: number }>;
  };
  registrationCompletion?: {
    eventId: string;
    termsAccepted: boolean;
    newsletterOptIn: boolean;
    triumphMarketingOptIn: boolean;
    movemberMarketingOptIn: boolean;
  };
}

export function getAdyenRedirectSessionStorageKey(sessionId: string): string {
  return `${ADYEN_REDIRECT_SESSION_STORAGE_PREFIX}:${sessionId}`;
}

function getSessionStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage;
    }

    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage;
    }
  } catch {
    return null;
  }

  return null;
}

export function saveAdyenRedirectSession(
  session: Omit<StoredAdyenRedirectSession, 'savedAt'>,
): void {
  const storage = getSessionStorage();
  if (!storage) return;

  try {
    storage.setItem(
      getAdyenRedirectSessionStorageKey(session.sessionId),
      JSON.stringify({
        ...session,
        savedAt: Date.now(),
      } satisfies StoredAdyenRedirectSession),
    );
  } catch {
    // Storage write failures should not block checkout.
  }
}

export function loadAdyenRedirectSession(sessionId: string): StoredAdyenRedirectSession | null {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(getAdyenRedirectSessionStorageKey(sessionId));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredAdyenRedirectSession>;
    if (
      parsed.sessionId !== sessionId ||
      typeof parsed.sessionData !== 'string' ||
      typeof parsed.donationId !== 'string' ||
      typeof parsed.savedAt !== 'number'
    ) {
      return null;
    }

    return {
      donationId: parsed.donationId,
      sessionId: parsed.sessionId,
      sessionData: parsed.sessionData,
      donorEmail: parsed.donorEmail || '',
      flow:
        parsed.flow === 'donation' || parsed.flow === 'registration' || parsed.flow === 'items'
          ? parsed.flow
          : undefined,
      amount: typeof parsed.amount === 'number' ? parsed.amount : 0,
      currencySymbol: typeof parsed.currencySymbol === 'string' ? parsed.currencySymbol : '',
      savedAt: parsed.savedAt,
      commitInputs: parsed.commitInputs,
      registrationCompletion: parsed.registrationCompletion,
    };
  } catch {
    return null;
  }
}

export function clearAdyenRedirectSession(sessionId: string): void {
  const storage = getSessionStorage();
  if (!storage) return;

  try {
    storage.removeItem(getAdyenRedirectSessionStorageKey(sessionId));
  } catch {
    // Storage delete failures should not block checkout.
  }
}

// ─── In-flight payment recovery (localStorage) ───
//
// Survives native-app switches on mobile where sessionStorage is wiped.
// Used as a minimal signal to detect "we were mid-payment" on cold reload.

const INFLIGHT_KEY = 'adyen-inflight-payment';
const INFLIGHT_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes

interface InFlightPayment {
  donationId: string;
  sessionId: string;
  savedAt: number;
}

function getLocalStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }

    if (typeof localStorage !== 'undefined') {
      return localStorage;
    }
  } catch {
    return null;
  }

  return null;
}

export function saveInFlightPayment(donationId: string, sessionId: string): void {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    storage.setItem(
      INFLIGHT_KEY,
      JSON.stringify({ donationId, sessionId, savedAt: Date.now() } satisfies InFlightPayment),
    );
  } catch {
    // Storage write failures should not block checkout.
  }
}

export function loadInFlightPayment(): InFlightPayment | null {
  const storage = getLocalStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(INFLIGHT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<InFlightPayment>;
    if (
      typeof parsed.donationId !== 'string' ||
      typeof parsed.sessionId !== 'string' ||
      typeof parsed.savedAt !== 'number'
    ) {
      return null;
    }

    if (Date.now() - parsed.savedAt > INFLIGHT_EXPIRY_MS) {
      storage.removeItem(INFLIGHT_KEY);
      return null;
    }

    return parsed as InFlightPayment;
  } catch {
    return null;
  }
}

export function clearInFlightPayment(): void {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    storage.removeItem(INFLIGHT_KEY);
  } catch {
    // Storage delete failures should not block checkout.
  }
}
